/**
 * blogUiStrings — ecosystem SSOT for the blog UI chrome strings (BLOG-STD-001).
 *
 * The blog page components (BlogIndexPage / BlogPostPage / BlogCategoryPage /
 * BlogTagPage / BlogPostCard) render a small set of fixed chrome strings —
 * section headings, taxonomy eyebrows, empty/error messages, the retry button,
 * the reading-progress aria label, and the "{{minutes}} min read" template.
 * These are GENERIC (every tenant's blog says "Related posts" / "Retry"), so a
 * single ecosystem SSOT is the factory-first home — NOT per-app i18n bundles.
 *
 * Crucially this SSOT is consumed by BOTH the client path (`BlogTemplateRoute`,
 * which receives the active locale) AND the server prerender path
 * (`renderBlogHtml`, which receives the render locale). Because both paths
 * resolve the SAME strings for the SAME locale, the server-prerendered HTML and
 * the client hydration render byte-identical chrome — no hydration mismatch and
 * no app-bundle coupling for the Node prerender harness.
 *
 * The Spanish strings are machine translations (MES-001) pending native-speaker
 * review; the dynamic post/category/tag CONTENT is localized upstream via the
 * `marketingLocalizedStrings` overlay, separately from this chrome SSOT.
 *
 * @module @puffin-core/marketing-shell/blog/blogUiStrings
 */

/** Canonical key set — every locale object MUST define exactly these keys. */
export const BLOG_UI_STRING_KEYS = Object.freeze([
  'indexHeading',
  'emptyIndex',
  'relatedPostsHeading',
  'relatedUnavailable',
  'categoryEyebrow',
  'emptyCategory',
  'tagEyebrow',
  'postTagsLabel',
  'emptyTag',
  'errorHeading',
  'errorBodyIndex',
  'errorBodyPost',
  'errorBodyCategory',
  'errorBodyTag',
  'retry',
  'readingProgressLabel',
  'readingMinutesLabel',
]);

const EN = Object.freeze({
  indexHeading: 'From the blog',
  emptyIndex: 'No posts published yet.',
  relatedPostsHeading: 'Related posts',
  relatedUnavailable: 'Related posts could not be loaded.',
  categoryEyebrow: 'Category',
  emptyCategory: 'No posts in this category yet.',
  tagEyebrow: 'Tag',
  postTagsLabel: 'Tagged',
  emptyTag: 'No posts tagged with this label yet.',
  errorHeading: 'Something went wrong.',
  errorBodyIndex: "We couldn't load the blog. Please try again.",
  errorBodyPost: "We couldn't load this post.",
  errorBodyCategory: "We couldn't load this category.",
  errorBodyTag: "We couldn't load this tag.",
  retry: 'Retry',
  readingProgressLabel: 'Reading progress',
  readingMinutesLabel: '{{minutes}} min read',
});

// MES-001: machine translation pending native-speaker review.
const ES = Object.freeze({
  indexHeading: 'Del blog',
  emptyIndex: 'Aún no hay publicaciones.',
  relatedPostsHeading: 'Publicaciones relacionadas',
  relatedUnavailable: 'No se pudieron cargar las publicaciones relacionadas.',
  categoryEyebrow: 'Categoría',
  emptyCategory: 'Aún no hay publicaciones en esta categoría.',
  tagEyebrow: 'Etiqueta',
  postTagsLabel: 'Etiquetado en',
  emptyTag: 'Aún no hay publicaciones con esta etiqueta.',
  errorHeading: 'Algo salió mal.',
  errorBodyIndex: 'No pudimos cargar el blog. Inténtalo de nuevo.',
  errorBodyPost: 'No pudimos cargar esta publicación.',
  errorBodyCategory: 'No pudimos cargar esta categoría.',
  errorBodyTag: 'No pudimos cargar esta etiqueta.',
  retry: 'Reintentar',
  readingProgressLabel: 'Progreso de lectura',
  readingMinutesLabel: '{{minutes}} min de lectura',
});

const BY_LANGUAGE = Object.freeze({ en: EN, es: ES });

/** English string set — also the default/fallback bundle. */
export const DEFAULT_BLOG_UI_STRINGS = EN;

/**
 * Resolve the blog UI string set for a locale. Matches on the primary
 * language subtag (case-insensitive), so 'es', 'ES', and 'es-ES' all resolve
 * to Spanish; any unknown locale falls back to English.
 *
 * @param {string} [locale]
 * @returns {Readonly<Record<string, string>>}
 */
export function getBlogUiStrings(locale) {
  const subtag = typeof locale === 'string' ? locale.toLowerCase().split('-')[0] : '';
  return BY_LANGUAGE[subtag] || EN;
}
